import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db, storage } from '@/lib/firebase';
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  limit,
  doc,
  getDoc,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { ref as sref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '@/AuthContext';

/**
 * PreOrderChat
 * Customer↔Store (manager/packers) chat bound to preOrderThreads/{threadId}
 * ThreadId strategy: `${storeId}_${uid}` per user per store to avoid duplicates.
 */
export default function PreOrderChat({ storeId, storeType = 'cooperative', senderRole = 'customer', threadId: threadIdProp = '', ensureThread = true }) {
  const { user } = useAuth();
  const uid = user?.uid || '';
  const [threadId, setThreadId] = useState('');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [initializing, setInitializing] = useState(ensureThread ? true : false);
  const fileInputRef = useRef(null);
  const listRef = useRef(null);
  const markReadTimer = useRef(null);

  // Derive a stable thread id (user x store) unless overridden via props (staff use-case)
  useEffect(() => {
    if (threadIdProp) {
      setThreadId(threadIdProp);
      return;
    }
    if (!uid || !storeId) return;
    setThreadId(`${storeId}__${uid}`);
  }, [uid, storeId, threadIdProp]);

  // Ensure the thread exists with participants (customer flow). Staff should not attempt to create/modify threads.
  useEffect(() => {
    (async () => {
      if (!ensureThread) { // staff mode
        // Do not attempt to create/modify threads; ensure UI leaves initializing state
        setInitializing(false);
        return;
      }
      if (!threadId || !uid || !storeId) return;
      setInitializing(true);
      try {
        const tRef = doc(db, 'preOrderThreads', threadId);
        const snap = await getDoc(tRef);
        if (!snap.exists()) {
          await setDoc(tRef, {
            participants: [uid],
            customerUid: uid,
            storeId,
            storeType,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } else {
          // Make sure current user stays in participants
          const data = snap.data();
          if (!Array.isArray(data.participants) || !data.participants.includes(uid)) {
            await updateDoc(tRef, {
              participants: Array.from(new Set([...(data.participants || []), uid])),
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (e) {
        // Silent; UI will still render without messages
        console.warn('ensure preOrder thread failed', e);
      } finally {
        setInitializing(false);
      }
    })();
  }, [threadId, uid, storeId, storeType, ensureThread]);

  const msgsQuery = useMemo(() => {
    if (!threadId) return null;
    return query(collection(db, `preOrderThreads/${threadId}/messages`), orderBy('createdAt', 'asc'), limit(200));
  }, [threadId]);

  useEffect(() => {
    if (!msgsQuery) return;
    const unsub = onSnapshot(msgsQuery, (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(rows);
      setTimeout(() => { listRef.current?.lastElementChild?.scrollIntoView?.({ behavior: 'smooth' }); }, 10);
    });
    return () => unsub();
  }, [msgsQuery]);

  // Mark thread as read for current user when messages change (debounced) and on mount
  useEffect(() => {
    if (!uid || !threadId) return;
    // debounce frequent updates when many messages arrive at once
    if (markReadTimer.current) clearTimeout(markReadTimer.current);
    markReadTimer.current = setTimeout(async () => {
      try {
        await updateDoc(doc(db, 'preOrderThreads', threadId), { [`lastRead.${uid}`]: serverTimestamp() });
      } catch (e) {
        // ignore permission/network errors; will retry on next change
      }
    }, 400);
    return () => {
      if (markReadTimer.current) {
        clearTimeout(markReadTimer.current);
        markReadTimer.current = null;
      }
    };
  }, [uid, threadId, messages.length]);

  async function sendMessage({ attachmentUrl = null } = {}) {
    const content = (text || '').trim();
    if (!uid || !threadId) return;
    if (!content && !attachmentUrl) return;
    setSending(true);
    try {
      const msg = {
        text: content || null,
        attachmentUrl: attachmentUrl || null,
        senderUid: uid,
        senderRole: senderRole,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, `preOrderThreads/${threadId}/messages`), msg);
      // Optimistically update thread last message meta
      await updateDoc(doc(db, 'preOrderThreads', threadId), {
        lastMessage: content ? content.slice(0, 140) : (attachmentUrl ? '📎 Attachment' : null),
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }).catch(() => {});
      setText('');
    } catch (e) {
      console.warn('sendMessage failed', e);
    } finally {
      setSending(false);
    }
  }

  async function onPickImage(e) {
    const file = e?.target?.files?.[0];
    if (!file || !threadId) return;
    setUploading(true);
    try {
      const path = `chat-attachments/${threadId}/${Date.now()}_${file.name}`;
      const r = sref(storage, path);
      await uploadBytes(r, file, { contentType: file.type });
      const url = await getDownloadURL(r);
      await sendMessage({ attachmentUrl: url });
    } catch (e) {
      console.warn('upload image failed', e);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div className="flex flex-col h-[420px] border rounded-lg overflow-hidden">
      {/* Helper guide banner */}
      <div className="px-3 py-2 text-xs sm:text-sm bg-emerald-50 text-emerald-800 border-b border-emerald-200">
        Any inquiries or requests? You can type them here. Store managers will reply to you soon.
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
        {initializing ? (
          <div className="text-sm text-gray-500 text-center py-8">Preparing chat…</div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8">Ask us anything about products or availability.</div>
        ) : (
          messages.map(m => (
            <div key={m.id} className={`max-w-[80%] ${m.senderUid === uid ? 'ml-auto text-right' : ''}`}>
              <div className={`inline-block rounded-lg px-3 py-2 text-sm ${m.senderUid === uid ? 'bg-brand-50 border border-brand-200' : 'bg-white border'}`}>
                {m.attachmentUrl && (
                  <a href={m.attachmentUrl} target="_blank" rel="noreferrer" className="block mb-1">
                    <img src={m.attachmentUrl} alt="attachment" className="max-h-48 rounded" />
                  </a>
                )}
                {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="border-t p-2 flex items-center gap-2">
        <input
          type="text"
          className="input input-bordered flex-1"
          placeholder="Any inquiries or requests? Type here…"
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
        />
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
        <button type="button" className="btn btn-outline" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
          {uploading ? 'Uploading…' : 'Image'}
        </button>
        <button type="button" className="btn btn-primary" disabled={sending || (!text.trim())} onClick={() => sendMessage()}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
